import { FilterCondition } from '../../../types/packager-types';

// Filter conditions by API type (string, number, integer, object, array)
export const FILTER_CONDITIONS_BY_TYPE: Record<
  string,
  { value: FilterCondition; label: string }[]
> = {
  string: [
    { value: FilterCondition.EQUALS, label: 'equals' },
    { value: FilterCondition.IS_ONE_OF, label: 'is one of' },
    { value: FilterCondition.CONTAINS, label: 'contains' },
    { value: FilterCondition.NOT_CONTAINS, label: 'does not contain' },
  ],
  number: [
    { value: FilterCondition.EQUALS, label: 'equals' },
    { value: FilterCondition.BETWEEN, label: 'between' },
    { value: FilterCondition.GREATER_THAN, label: '>' },
    { value: FilterCondition.LESS_THAN, label: '<' },
  ],
  integer: [
    { value: FilterCondition.EQUALS, label: 'equals' },
    { value: FilterCondition.BETWEEN, label: 'between' },
    { value: FilterCondition.GREATER_THAN, label: '>' },
    { value: FilterCondition.LESS_THAN, label: '<' },
  ],
  // object and array types: limited filter support
  object: [{ value: FilterCondition.EQUALS, label: 'equals' }],
  array: [{ value: FilterCondition.CONTAINS, label: 'contains' }],
};

export const METADATA_PATHS = [
  { value: 'document.created_at', label: 'document.created_at' },
  { value: 'document.updated_at', label: 'document.updated_at' },
  { value: 'document.label', label: 'document.label' },
  { value: 'document.status', label: 'document.status' },
  { value: 'document.obfuscated_id', label: 'document.obfuscated_id' },
  { value: 'document_data.status', label: 'document_data.status' },
  { value: 'document_data.obfuscated_id', label: 'document_data.obfuscated_id' },
  { value: 'data_pool.label', label: 'data_pool.label' },
  { value: 'data_pool.obfuscated_id', label: 'data_pool.obfuscated_id' },
  { value: 'organization.label', label: 'organization.label' },
  { value: 'extraction_batch.label', label: 'extraction_batch.label' },
  { value: 'extraction_batch.verification_status', label: 'extraction_batch.verification_status' },
  { value: 'package.id', label: 'package.id' },
  { value: 'package.obfuscated_id', label: 'package.obfuscated_id' },
  { value: 'package.created_at', label: 'package.created_at' },
  { value: 'package.delivered_at', label: 'package.delivered_at' },
];

// Map API condition strings to FilterCondition enum values
const CONDITION_STRING_TO_ENUM: Record<string, FilterCondition> = {
  equals: FilterCondition.EQUALS,
  is_one_of: FilterCondition.IS_ONE_OF,
  contains: FilterCondition.CONTAINS,
  not_contains: FilterCondition.NOT_CONTAINS,
  between: FilterCondition.BETWEEN,
  greater_than: FilterCondition.GREATER_THAN,
  less_than: FilterCondition.LESS_THAN,
};

// Map FilterCondition values to display labels
const CONDITION_LABELS: Record<FilterCondition, string> = {
  [FilterCondition.EQUALS]: 'equals',
  [FilterCondition.IS_ONE_OF]: 'is one of',
  [FilterCondition.CONTAINS]: 'contains',
  [FilterCondition.NOT_CONTAINS]: 'does not contain',
  [FilterCondition.BETWEEN]: 'between',
  [FilterCondition.GREATER_THAN]: '>',
  [FilterCondition.LESS_THAN]: '<',
};

export const getFilterConditionsForField = (
  fieldName: string,
  fields: { name: string; type: string; filter_conditions?: string[] }[]
): { value: FilterCondition; label: string }[] => {
  const field = fields.find((f) => f.name === fieldName);

  // If API provides filter_conditions, use them
  if (field?.filter_conditions && field.filter_conditions.length > 0) {
    return field.filter_conditions
      .map((condition) => {
        const enumValue = CONDITION_STRING_TO_ENUM[condition];
        if (enumValue) {
          return { value: enumValue, label: CONDITION_LABELS[enumValue] };
        }
        return null;
      })
      .filter((c): c is { value: FilterCondition; label: string } => c !== null);
  }

  // Fall back to type-based conditions
  const fieldType = field?.type || 'string';
  return FILTER_CONDITIONS_BY_TYPE[fieldType] || FILTER_CONDITIONS_BY_TYPE.string;
};
